import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const Menu = ({
  trigger,
  closeTrigger,
  menuContainerId,
  menuContainerClassName,
  children
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusIndex, setFocusIndex] = React.useState();
  const itemsRef = React.useRef([]);
  const menuRef = React.useRef();

  const arrayChildren = React.Children.toArray(children);

  React.useEffect(() => {
    if (focusIndex !== undefined) {
      itemsRef.current[focusIndex]?.focus();
    }
  }, [focusIndex]);

  React.useEffect(() => {
    const clickHandler = (event) => {
      if (isOpen && (!menuRef.current || !menuRef.current.contains(event.target))) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', clickHandler);
    document.addEventListener('focus', clickHandler, true);
    return () => {
      document.removeEventListener('click', clickHandler);
      document.removeEventListener('focus', clickHandler, true);
    };
  }, [isOpen]);

  const handleTriggerKeyUp = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setFocusIndex(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setFocusIndex(Math.max(0, itemsRef.current.length - 1));
    }
  };

  const handleKeyUp = (e) => {
    if (isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newFocusIndex = ((focusIndex || 0) + 1) % itemsRef.current.length;
        setFocusIndex(newFocusIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newFocusIndex = ((focusIndex || 0) - 1) % itemsRef.current.length;
        setFocusIndex(newFocusIndex < 0 ? itemsRef.current.length - 1 : newFocusIndex);
      }
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusIndex(undefined);
      itemsRef.current = [];
    } else {
      setFocusIndex(0);
    }
  };

  const assignRef = (element) => {
    if (element && !itemsRef.current.includes(element)) {
      itemsRef.current.push(element);
    }
  };

  return (
    <>
      {React.cloneElement(isOpen ? closeTrigger : trigger, {
        onClick: toggleMenu,
        onKeyDown: handleTriggerKeyUp,
        'aria-haspopup': true,
        'aria-expanded': isOpen,
        'aria-controls': menuContainerId
      })}
      {isOpen && createPortal(
        <div role="presentation" onKeyDown={handleKeyUp} ref={menuRef} className={menuContainerClassName}>
          {React.Children.map(arrayChildren, (child) => React.cloneElement(child, child.props.tabIndex
            ? {
              ref: (el) => assignRef(el),
              onClick: (e) => {
                setIsOpen(false);
                if (child.props.onClick) {
                  child.props.onClick(e);
                }
              }
            }
            : {}))}
        </div>,
        document.getElementById(menuContainerId)
      )}
    </>
  );
};

Menu.propTypes = {
  trigger: PropTypes.element.isRequired,
  closeTrigger: PropTypes.element.isRequired,
  menuContainerId: PropTypes.string.isRequired,
  menuContainerClassName: PropTypes.string,
  children: PropTypes.element.isRequired
};

Menu.defaultProps = {
  menuContainerClassName: ''
};

export default Menu;
